import { Menu, Tooltip } from '@mantine/core';
import { useKeycloak } from '@react-keycloak/web';
import c from 'classnames';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { CurrentCompanyContext } from '../hooks/CurrentCompanyContext';
import { ROUTES } from '../routes/routes';
import { UserPlan } from '../utils/plans';
import IconCollapse from './icons/IconCollapse';
import IconCustomerPortal from './icons/IconCustomerPortal';
import IconDashboard from './icons/IconDashboard';
import IconExpand from './icons/IconExpand';
import IconFeatureFlags from './icons/IconFeatureFlags';
import IconIntegrations from './icons/IconIntegrations';
import IconPricingPages from './icons/IconPricingPages';
import IconSupport from './icons/IconSupport';
import IconTool from './icons/IconTool';
import IconTest from './icons/IconTest';

const hasPaidPlan = (plan?: UserPlan) =>
    plan && [UserPlan.BOOTSTRAPPER, UserPlan.SCALE_UP, UserPlan.STARTUP].includes(plan);

interface Props {
    currentPlan?: UserPlan;
    showUpgradePopup: () => void;
}

export default function Sidebar({ currentPlan, showUpgradePopup }: Props) {
    const { keycloak } = useKeycloak();
    const [expanded, setExpanded] = useState(true);
    const { pathname } = useLocation();
    const handleToggleExpand = () => setExpanded(!expanded);
    useEffect(() => {
        if (pathname !== ROUTES.DASHBOARD) {
            setExpanded(false);
        }
    }, [pathname]);

    const navigateTo = useNavigateTo();
    return (
        <>
            {!!keycloak?.authenticated && (
                <aside
                    className={c(
                        'relative bg-sidebar min-h-screen hidden sm:block shadow-xl overflow-y-scroll overflow-x-visible',
                        {
                            'w-64': expanded,
                        }
                    )}
                >
                    <button
                        type="button"
                        onClick={handleToggleExpand}
                        className="absolute right-2 top-2 cursor-pointer text-slate-100"
                    >
                        {expanded ? <IconCollapse className="h-4 w-4" /> : <IconExpand className="h-4 w-4" />}
                        <span className="sr-only">{expanded ? 'Collapse' : 'Expand'} sidebar</span>
                    </button>
                    <div className={c({ 'p-6': expanded, 'pt-12': !expanded })}>
                        {expanded && (
                            <a
                                href={ROUTES.DASHBOARD}
                                onClick={navigateTo(ROUTES.DASHBOARD)}
                                className="text-white text-3xl uppercase hover:text-gray-300"
                            >
                                <img src="/images/pricewell-logo-white.png" alt="PriceWell logo" />
                            </a>
                        )}
                        <CreateButton
                            currentPlan={currentPlan}
                            showUpgradePopup={showUpgradePopup}
                            expanded={expanded}
                        />
                    </div>
                    <nav className="text-white text-base pt-3">
                        <NavItems currentPlan={currentPlan} showUpgradePopup={showUpgradePopup} expanded={expanded} />
                    </nav>
                </aside>
            )}
        </>
    );
}

export function CreateButton({
    currentPlan,
    showUpgradePopup,
    expanded = true,
}: {
    currentPlan?: UserPlan;
    showUpgradePopup: () => void;
    expanded?: boolean;
}) {
    const company = useContext(CurrentCompanyContext);
    const navigateTo = useNavigateTo();
    const handleShowUpgrade = () => {
        showUpgradePopup();
    };
    const hasStripeConnection = !!company?.stripe?.stripeId;

    return (
        <>
            <div className="flex flex-wrap">
                <div className="w-full">
                    <Menu withinPortal>
                        <Menu.Target>
                            <button
                                className={c(
                                    'bg-white text-black active:bg-grey-600 font-bold uppercase text-xs rounded shadow hover:shadow-md outline-none focus:outline-none m-1 ease-linear transition-all duration-150',
                                    { 'px-4 py-2': expanded, 'px-2 py-1': !expanded }
                                )}
                                type="button"
                            >
                                Create{' '}
                                {expanded && (
                                    <>
                                        New{' '}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 1792 1792"
                                            className="h-6 w-6 text-gray-800 inline"
                                            stroke="currentColor"
                                        >
                                            <path d="M1395 736q0 13-10 23l-466 466q-10 10-23 10t-23-10L407 759q-10-10-10-23t10-23l50-50q10-10 23-10t23 10l393 393 393-393q10-10 23-10t23 10l50 50q10 10 10 23z" />
                                        </svg>
                                    </>
                                )}
                            </button>
                        </Menu.Target>

                        <Menu.Dropdown>
                            <Menu.Item onClick={navigateTo(ROUTES.PAGES_NEW)}>Pricing Page</Menu.Item>
                            {hasStripeConnection && (
                                <>
                                    <Menu.Item
                                        onClick={
                                            hasPaidPlan(currentPlan)
                                                ? navigateTo(ROUTES.CUSTOMER_PORTALS_NEW)
                                                : handleShowUpgrade
                                        }
                                    >
                                        Customer Portal
                                    </Menu.Item>
                                    <Menu.Item
                                        onClick={
                                            hasPaidPlan(currentPlan)
                                                ? navigateTo(ROUTES.GATED_CONTENT)
                                                : handleShowUpgrade
                                        }
                                    >
                                        Gated Content
                                    </Menu.Item>
                                </>
                            )}
                        </Menu.Dropdown>
                    </Menu>
                </div>
            </div>
        </>
    );
}

export function NavItems({
    currentPlan,
    onClick,
    showUpgradePopup,
    expanded = true,
}: {
    currentPlan?: UserPlan;
    onClick?: () => void;
    showUpgradePopup: () => void;
    expanded?: boolean;
}) {
    const { pathname } = useLocation();
    const navigateTo = useNavigateTo();
    const company = useContext(CurrentCompanyContext);
    const { addToast } = useToasts();
    const hasStripeConnection = !!company?.stripe?.stripeId;
    const handleShowConnectStripe = () => {
        addToast('You must connect your Stripe account to use this feature.', {
            appearance: 'warning',
        });
    };
    const handleClickSupport = () => {
        onClick?.();
        window.$crisp.push(['do', 'chat:open']);
    };

    return (
        <>
            <Tooltip position="right" withArrow withinPortal disabled={expanded} offset={-10} label="Dashboard">
                <a
                    href={ROUTES.DASHBOARD}
                    onClick={navigateTo(ROUTES.DASHBOARD, onClick)}
                    className={c('flex items-center text-white py-4 pl-6 nav-item', {
                        'active-nav-link': pathname === ROUTES.DASHBOARD,
                    })}
                >
                    <IconDashboard className="mr-3 w-5" />
                    <span className={c({ 'sr-only': !expanded })}>Dashboard</span>
                </a>
            </Tooltip>
            <Tooltip position="right" withArrow withinPortal disabled={expanded} offset={-10} label="Pricing Pages">
                <a
                    href={ROUTES.PAGES}
                    onClick={navigateTo(ROUTES.PAGES, onClick)}
                    className={c('flex items-center text-white py-4 pl-6 nav-item', {
                        'active-nav-link': pathname?.includes(ROUTES.PAGES),
                    })}
                >
                    <IconPricingPages className="mr-3 w-5" />
                    <span className={c({ 'sr-only': !expanded })}>Pricing Pages</span>
                </a>
            </Tooltip>
            <Tooltip position="right" withArrow withinPortal disabled={expanded} offset={-10} label="Customer Portals">
                <button
                    onClick={
                        hasStripeConnection
                            ? hasPaidPlan(currentPlan)
                                ? navigateTo(ROUTES.CUSTOMER_PORTALS, onClick)
                                : showUpgradePopup
                            : handleShowConnectStripe
                    }
                    className={c('flex items-center py-4 pl-6 nav-item  w-full', {
                        'active-nav-link': pathname?.includes(ROUTES.CUSTOMER_PORTALS),
                        'text-gray-500': !hasStripeConnection,
                        'text-white': hasStripeConnection,
                    })}
                >
                    <IconCustomerPortal className="mr-3 w-5" />
                    <span className={c({ 'sr-only': !expanded })}>Customer Portals</span>
                </button>
            </Tooltip>
            <Tooltip position="right" withArrow withinPortal disabled={expanded} offset={-10} label="Gated Content">
                <button
                    onClick={
                        hasStripeConnection
                            ? hasPaidPlan(currentPlan)
                                ? navigateTo(ROUTES.GATED_CONTENT, onClick)
                                : showUpgradePopup
                            : handleShowConnectStripe
                    }
                    className={c('flex items-center py-4 pl-6 nav-item w-full', {
                        'active-nav-link': pathname?.includes(ROUTES.GATED_CONTENT),
                        'text-gray-500': !hasStripeConnection,
                        'text-white': hasStripeConnection,
                    })}
                >
                    <IconFeatureFlags className="mr-3 w-5" />
                    <span className={c({ 'sr-only': !expanded })}>Gated Content</span>
                </button>
            </Tooltip>
            <Tooltip position="right" withArrow withinPortal disabled={expanded} offset={-10} label="A/B Tests">
                <button
                    onClick={hasStripeConnection ? navigateTo(ROUTES.EXPERIMENTS, onClick) : handleShowConnectStripe}
                    className={c('flex items-center py-4 pl-6 nav-item w-full', {
                        'active-nav-link': pathname?.includes(ROUTES.EXPERIMENTS),
                        'text-gray-500': !hasStripeConnection,
                        'text-white': hasStripeConnection,
                    })}
                >
                    <IconTest className="mr-3 w-5" />
                    <span className={c({ 'sr-only': !expanded })}>A/B Tests</span>
                </button>
            </Tooltip>
            <Tooltip position="right" withArrow withinPortal disabled={expanded} offset={-10} label="Tools">
                <button
                    onClick={hasStripeConnection ? navigateTo(ROUTES.TOOLS, onClick) : handleShowConnectStripe}
                    className={c('flex items-center py-4 pl-6 nav-item w-full', {
                        'active-nav-link': pathname?.includes(ROUTES.TOOLS),
                        'text-gray-500': !hasStripeConnection,
                        'text-white': hasStripeConnection,
                    })}
                >
                    <IconTool className="mr-3 w-5" />
                    <span className={c({ 'sr-only': !expanded })}>Tools</span>
                </button>
            </Tooltip>
            <Tooltip position="right" withArrow withinPortal disabled={expanded} offset={-10} label="Integrations">
                <button
                    onClick={hasPaidPlan(currentPlan) ? navigateTo(ROUTES.INTEGRATIONS, onClick) : showUpgradePopup}
                    className={c('flex items-center text-white py-4 pl-6 nav-item w-full', {
                        'active-nav-link': pathname?.includes(ROUTES.INTEGRATIONS),
                    })}
                >
                    <IconIntegrations className="mr-3 w-5" />
                    <span className={c({ 'sr-only': !expanded })}>Integrations</span>
                </button>
            </Tooltip>
            <Tooltip position="right" withArrow withinPortal disabled={expanded} offset={-10} label="Support">
                <button
                    onClick={handleClickSupport}
                    className={c('flex items-center text-white py-4 pl-6 nav-item w-full', {
                        'active-nav-link': pathname?.includes(ROUTES.HELP),
                    })}
                >
                    <IconSupport className="mr-3 w-5" />
                    <span className={c({ 'sr-only': !expanded })}>Support</span>
                </button>
            </Tooltip>
            <Tooltip position="right" withArrow withinPortal disabled={expanded} offset={-10} label="Affiliate Program">
                <a
                    href="https://pricewell.getrewardful.com/signup"
                    className={'flex items-center text-white py-4 pl-6 nav-item w-full'}
                >
                    💰 <span className={c({ 'sr-only': !expanded })}>Affiliate Program</span>
                </a>
            </Tooltip>
        </>
    );
}

export const useNavigateTo = () => {
    const history = useHistory();
    return useCallback(
        (url: string, before?: () => void) => (e: React.MouseEvent<HTMLElement>) => {
            before?.();
            e?.stopPropagation();
            e?.preventDefault();
            e?.nativeEvent?.stopImmediatePropagation();
            history.push(url);
        },
        [history]
    );
};
